import React, { useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import * as charts from 'echarts';
import { getFormattedDateArray } from '../../../shared/lib/dateUtils';
import { useTheme } from '../../../contexts/ThemeContext'
import { useMobile } from '../../../contexts/MobileContext';
import { getAxisChartGrid, getChartLegendLayout, getChartSemanticColors, getChartTheme, getChartTooltipMarker, getChartTooltipStyle, getChartTypography, getFadedLineStroke } from '../charting/chartTheme';
import useChartAutoResize from '../charting/useChartAutoResize';

const getOption = (specs, theme, isMobile, showLegend = true) => {
  const colors = getChartSemanticColors(theme);
  const typography = getChartTypography(isMobile);
  const {values, lastDay} = specs;

  const botClicks = values.bot;
  const humanClicks = values.human;
  const humanLabel = 'Human Traffic';
  const botLabel = 'Bot Traffic';
  const palette = [colors.human, colors.bot];

  return {
    tooltip: getChartTooltipStyle(theme, typography, {
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      formatter: function (params) {
        let tooltipContent = '';

        const date = new Date(params[0].name + " 2024");
        const month = date.toLocaleString('default', { month: 'long' });
        const day = date.getDate();
        tooltipContent += `${month} ${day} <br/>`;

        params.forEach((item) => {
          const name = item.seriesName.startsWith('Human') ? 'Human' : 'Bot';
          const color = item.seriesName.startsWith('Human') ? colors.human : colors.bot;

          const value = item.value;
          tooltipContent += `
          ${getChartTooltipMarker(color)}
          ${name}: ${value}<br/>
        `;
        });

        return tooltipContent;
      },
    }),
    ...(showLegend ? {
      legend: {
        ...getChartLegendLayout(isMobile, theme),
        data: [
          {icon: 'square', name: humanLabel},
          {icon: 'square', name: botLabel}
        ],
        textStyle: {
          color: theme.textHigh,
          fontWeight: typography.strongWeight,
          fontSize: typography.legendSize,
        },
      },
    } : {}),
    xAxis: {
      name: '',
      type: 'category',
      data: getFormattedDateArray(lastDay, botClicks.length),
      axisLine: {
        lineStyle: {
          color: theme.strokeMid,
          width: 1,
        },
      },
      axisLabel: {
        color: theme.textHigh,
        boundaryGap: false,
        interval: Math.ceil(botClicks.length / 4),
        fontSize: typography.axisLabelSize,
        rotate: isMobile ? 30 : 0,
        hideOverlap: true,
      },
      boundaryGap: false,
      splitLine: {
        show: true,
        lineStyle: {
          color: theme.strokeLow,
          type: 'dashed',
          width: 1,
        },
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitNumber: isMobile ? 3 : 4,
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: theme.textHigh,
        formatter: (value) => {
          return value >= 1000 ? `${value / 1000}k` : value;
        },
        fontSize: typography.axisLabelSize,
        hideOverlap: true,
      },
      splitLine: {
        lineStyle: {
          color: theme.strokeLow,
          type: 'dashed',
          width: 1,
        },
      },
    },
    grid: getAxisChartGrid(isMobile, { hasLegend: showLegend }),
    color: palette,
    series: [
      {
        name: humanLabel,
        symbol: 'circle',
        symbolSize: 10,
        type: 'line',
        data: humanClicks,
        smooth: 0.15,
        showSymbol: false,
        lineStyle: {
          width: isMobile ? 1.25 : 1.75,
          color: getFadedLineStroke(charts, palette[0]),
        },
      },
      {
        name: botLabel,
        symbol: 'circle',
        symbolSize: 10,
        type: 'line',
        data: botClicks,
        smooth: 0.15,
        showSymbol: false,
        lineStyle: {
          width: isMobile ? 1.25 : 1.75,
          color: getFadedLineStroke(charts, palette[1]),
        },
      },
    ],
    backgroundColor: 'transparent',
  }
}

const LineGraph = ({specs, showLegend = true}) => {
  let { isDarkMode } = useTheme();
  const { isMobile } = useMobile();
  const chartRef = useRef(null);
  const theme = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);
  const chartOption = useMemo(
    () => getOption(specs, theme, isMobile, showLegend),
    [theme, isMobile, specs, showLegend]
  );

  useChartAutoResize(chartRef, [isDarkMode, isMobile, specs, showLegend]);

  return (
    <ReactECharts ref={chartRef}
                  option={chartOption}
                  opts={{ renderer: isMobile ? 'svg' : 'canvas' }}
                  notMerge={true}
                  key={`${isDarkMode ? 'dark' : 'light'}-${isMobile ? 'm' : 'd'}`}
                  className="chart site-overview-chart-canvas"
                  style={{ width: '100%', height: '100%' , minHeight: '300px'}} />
  );
}

export default LineGraph;
